'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type User, type Organization } from './api';

interface AuthState {
  user: User | null;
  organizations: Organization[];
  accessToken: string | null;
  refreshToken: string | null;
  currentOrgId: string | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (
    email: string,
    password: string,
  ) => Promise<{ user: User; organizations: Organization[] }>;
  logout: () => Promise<void>;
  setCurrentOrg: (orgId: string) => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'mpange_auth';

function loadStored(): Partial<AuthState> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStored(state: Partial<AuthState>) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      accessToken: state.accessToken,
      refreshToken: state.refreshToken,
      currentOrgId: state.currentOrgId,
    }),
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    user: null,
    organizations: [],
    accessToken: null,
    refreshToken: null,
    currentOrgId: null,
    loading: true,
  });

  const refreshAuth = useCallback(async () => {
    const stored = loadStored();
    if (!stored.accessToken) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    try {
      const { data } = await api.me(stored.accessToken);
      setState({
        user: data.user,
        organizations: data.organizations,
        accessToken: stored.accessToken ?? null,
        refreshToken: stored.refreshToken ?? null,
        currentOrgId:
          stored.currentOrgId ??
          data.organizations[0]?.id ??
          null,
        loading: false,
      });
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setState({
        user: null,
        organizations: [],
        accessToken: null,
        refreshToken: null,
        currentOrgId: null,
        loading: false,
      });
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const login = async (email: string, password: string) => {
    const { data } = await api.login({ email, password });
    const newState = {
      user: data.user,
      organizations: data.organizations,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      currentOrgId: data.organizations[0]?.id ?? null,
      loading: false,
    };
    setState(newState);
    saveStored(newState);
    return { user: data.user, organizations: data.organizations };
  };

  const logout = useCallback(async () => {
    const accessToken = state.accessToken;
    const refreshToken = state.refreshToken;

    localStorage.removeItem(STORAGE_KEY);
    queryClient.clear();
    setState({
      user: null,
      organizations: [],
      accessToken: null,
      refreshToken: null,
      currentOrgId: null,
      loading: false,
    });

    if (accessToken && refreshToken) {
      void api.logout(accessToken, refreshToken).catch(() => {});
    }
  }, [queryClient, state.accessToken, state.refreshToken]);

  const setCurrentOrg = (orgId: string) => {
    setState((s) => {
      const next = { ...s, currentOrgId: orgId };
      saveStored(next);
      return next;
    });
  };

  return (
    <AuthContext.Provider
      value={{ ...state, login, logout, setCurrentOrg, refreshAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useRequireAuth() {
  const auth = useAuth();
  return auth;
}
